import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "carrier" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form);
      toast.success("Account created!");
      nav(user.role === "shipper" ? "/shipper" : "/onboarding");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-xd-bg text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <span className="grid place-items-center w-9 h-9 rounded-md bg-xd-orange text-white"><Truck size={20} /></span>
          <span className="font-heading font-black text-lg">XD <span className="text-xd-orange">FREIGHT</span></span>
        </Link>
        <h1 className="font-heading text-3xl font-black tracking-tight text-center">Join XD FREIGHT</h1>
        <p className="mt-2 text-xd-muted text-sm text-center">Create your account to get started.</p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {[["carrier", "I'm a Carrier"], ["shipper", "I'm a Shipper"]].map(([val, label]) => (
            <button key={val} data-testid={`role-${val}`} onClick={() => setForm({ ...form, role: val })}
              className={`rounded-lg border p-4 text-sm font-semibold transition-colors ${form.role === val ? "border-xd-orange bg-xd-orange/10 text-xd-orange" : "border-xd-border bg-xd-paper text-xd-muted hover:border-white/30"}`}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-xd-muted">{form.role === "shipper" ? "Company name" : "Full name / Company"}</Label>
            <Input data-testid="reg-name" required value={form.name} onChange={set("name")} className="mt-1.5 bg-xd-surface border-xd-border h-11" />
          </div>
          <div>
            <Label className="text-xd-muted">Email</Label>
            <Input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 bg-xd-surface border-xd-border h-11" />
          </div>
          <div>
            <Label className="text-xd-muted">Phone</Label>
            <Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 bg-xd-surface border-xd-border h-11" />
          </div>
          <div>
            <Label className="text-xd-muted">Password</Label>
            <Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 bg-xd-surface border-xd-border h-11" />
          </div>
          <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full h-11 bg-xd-orange hover:bg-xd-orangeH text-white font-bold">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Create Account"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-xd-muted text-center">Already have an account? <Link to="/login" className="text-xd-orange font-semibold">Log in</Link></p>
      </div>
    </div>
  );
}
